import { Reveal } from "@/lib/motion";
import { useRef } from "react";
import { useInView } from "framer-motion";
import { ArrowRight, TrendingUp, Gauge, MousePointerClick, Smartphone } from "lucide-react";
import { useCountUp, ScoreRing } from "@/components/ScoreRing";

const METRICS = [
  { icon: TrendingUp, label: "SEO Score", from: 62, to: 94 },
  { icon: Gauge, label: "Performance", from: 43, to: 91 },
  { icon: MousePointerClick, label: "Conversion Rate", from: 38, to: 79 },
  { icon: Smartphone, label: "Mobile Usability", from: 67, to: 98 },
];

const BEFORE_IMG = "https://images.unsplash.com/photo-1631507623112-0092cef9c70d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBoZWFsdGhjYXJlJTIwY2xpbmljJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzg3ODAwMjEwfDA&ixlib=rb-4.1.0&q=85";
const AFTER_IMG = "https://images.unsplash.com/photo-1735469157670-1212e570eadc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwc2NyZWVufGVufDB8fHx8MTc4NzgwMDIxMHww&ixlib=rb-4.1.0&q=85";

function Metric({ icon: Icon, label, from, to }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const val = useCountUp(to, inView);
  return (
    <div ref={ref} className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <Icon size={17} className="text-slate-400" />
      <span className="text-sm font-medium text-slate-600 flex-1">{label}</span>
      <span className="font-mono text-slate-400 text-sm">{from}</span>
      <ArrowRight size={13} className="text-slate-300" />
      <span className="font-heading font-bold text-lg text-green-600 w-10 text-right">{val}</span>
    </div>
  );
}

function Frame({ img, score, tag }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-200 aspect-[4/3]">
      <img src={img} alt={tag} loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#5C162E]/40" />
      <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-slate-700 px-2 py-0.5 rounded">{tag}</span>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white rounded-full p-1.5 soft-shadow">
          <ScoreRing score={score} size={72} stroke={7} showLabel={false} />
        </div>
      </div>
    </div>
  );
}

export default function CaseStudies() {
  return (
    <section className="relative z-10 py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-3">
            <span className="overline">Real Results</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mt-3 leading-tight">
              Transforming Websites. Driving Business Growth.
            </h2>
            <button className="btn-outline rounded-xl px-6 py-3.5 mt-6 flex items-center gap-2 text-sm" data-testid="case-more-btn">
              View More Case Studies <ArrowRight size={15} />
            </button>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <Frame img={BEFORE_IMG} score={43} tag="Before" />
              <ArrowRight className="text-slate-300" />
              <Frame img={AFTER_IMG} score={91} tag="After" />
            </div>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-4">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              {METRICS.map((m) => <Metric key={m.label} {...m} />)}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
